import { createApplicationLayoutComposition } from './layouts/create-application-layout-composition.ts';
// dependency-graph-ignore-file
import '@atlaskit/css-reset';

import { initializeBridgeData } from '@databases/database-state/state/bridge-data';
import { getAppModeFromQueryParams } from '@databases/modes';
import { appStore } from '@databases/redux/store';
import { selectDatabaseByContentIdStatus } from '@databases/utils/redux/databases/selectors';
import { AsyncOperationStatus } from '@databases/utils/redux/redux';
import React from 'react';

import AppWithLocale from './app/components/app-with-locale.tsx';
import { createDatabasesApp } from './app/create-databases-app.tsx';
import { dispatchInitialDatabaseLoad } from './bootstrap/dispatch-initial-database-load.ts';
import { getModeConfig } from './bootstrap/get-mode-config.ts';
import { initializeCurrentDatabaseSdk } from './bootstrap/initialize-current-database-sdk.ts';
import { initializePermissionsManager } from './bootstrap/initialize-permissions-manager.ts';
import { initializeRecyclableDatabasePaywallFeatures } from './bootstrap/initialize-recyclable-database-paywall-features.ts';
import { initializeRecyclableSelectionsMachine } from './bootstrap/initialize-recyclable-selections-machine.ts';
import { initializeTableHighlightsController } from './bootstrap/initialize-table-highlights-controller.ts';
import { registerRecyclableModules } from './bootstrap/register-recyclable-modules.ts';

declare global {
  interface Window {
    previewDatabaseContentId?: string;
    previewRendered: boolean;
    isPreview?: boolean;
    previewAPI: PreviewAPI;
    aiFirstCreation: AiFirstCreationWindowAPI;
    importedCsv: string;
  }

  interface PreviewAPI {
    importFromCsv: (csv: string) => Promise<void>;
    editFromCsv: (contextSvg: string, resultSvg: string) => Promise<void>;
    exportToCsv: () => Promise<string>;
    transformToContext: (json: string) => Promise<string>;
    importFromJSON: (json: string) => Promise<void>;
    editFromPayload: (
      initialPayload: { type: 'context' | 'storage'; data: string },
      editPayload: { type: 'context'; data: string },
    ) => Promise<void>;
    getErrors: () => Promise<any[]>;
    clearErrors: () => Promise<void>;
  }

  interface AiFirstCreationWindowAPI {
    importFromString: (
      csvString: string,
      operationType: 'create' | 'edit',
      delay?: number,
    ) => Promise<void>;
    transformToContext: (json: string) => Promise<string>;
    importFromJSON: (json: string, contentId: string) => Promise<void>;
    readContent: (contentId: string) => Promise<string>;
    getErrors: () => unknown[];
    clearErrors: () => void;
    duplicateDatabase: (params?: { name?: string; spaceKey?: string }) => Promise<string>;
  }
}

const start = async () => {
  const contentId = `preview-${Date.now()}`;
  window.isPreview = true;
  Object.defineProperty(window, 'previewRendered', {
    get: () => {
      try {
        const status =
          selectDatabaseByContentIdStatus(appStore.getState(), {
            contentId,
          })?.status === AsyncOperationStatus.SUCCEEDED;

        return status;
      } catch (e) {
        console.error('Failed to get preview ready status:', e);
        return false;
      }
    },
  });

  window.previewDatabaseContentId = contentId;

  Object.defineProperty(window, 'frameElement', {
    value: document.body,
    writable: false,
  });

  document.body.setAttribute(
    'data-bridge-data',
    JSON.stringify({
      contentId,
      cloudId: 'preview-cloud',
      activationId: 'preview-activation',
      spaceKey: 'PREVIEW',
      spaceId: '0',
      spaceName: 'Preview',
      title: 'Preview Database',
      userId: 'preview-user',
      identity: {
        accountId: 'preview-user',
        accountType: 'atlassian' as any,
      },
      token: {
        accessToken: 'preview-token',
        container: 'ari:cloud:confluence:preview-cloud:space/1',
        board: 'ari:cloud:confluence:preview-cloud:database/1',
      },
      previewDatabase: true,
    }),
  );

  async function waitForSettled() {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    await new Promise((resolve) => requestAnimationFrame(resolve));
    await new Promise((resolve) => requestAnimationFrame(resolve));
  }

  window.previewAPI = {
    importFromCsv: async (csv: string) => {
      await waitForSettled();
      await window.aiFirstCreation.importFromString(csv, 'create', 0);
      await waitForSettled();
      window.importedCsv = csv;
    },
    editFromCsv: async (initialCsv: string, editCsv: string) => {
      await waitForSettled();
      await window.aiFirstCreation.importFromString(initialCsv, 'create', 0);
      await waitForSettled();
      await window.aiFirstCreation.importFromString(editCsv, 'edit', 0);
      await waitForSettled();
      window.importedCsv = editCsv;
    },
    editFromPayload: async (
      initialPayload: { type: 'context' | 'storage'; data: string },
      editPayload: { type: 'context'; data: string },
    ) => {
      await waitForSettled();

      if (initialPayload.type === 'context') {
        await window.aiFirstCreation.importFromString(initialPayload.data, 'create', 0);
      } else {
        await window.aiFirstCreation.importFromJSON(initialPayload.data, contentId);
      }

      await waitForSettled();
      await window.aiFirstCreation.importFromString(editPayload.data, 'edit', 0);
      await waitForSettled();
      window.importedCsv = editPayload.data;
    },
    exportToCsv: async () => {
      return await window.aiFirstCreation.readContent(contentId);
    },
    transformToContext: async (json: string) => {
      return await window.aiFirstCreation.transformToContext(json);
    },
    importFromJSON: async (json: string) => {
      await waitForSettled();
      await window.aiFirstCreation.importFromJSON(json, contentId);
      await waitForSettled();
    },
    getErrors: async () => {
      return await window.aiFirstCreation.getErrors();
    },
    clearErrors: async () => {
      return await window.aiFirstCreation.clearErrors();
    },
  };

  const run = createDatabasesApp(async ({ bootstrap, mount }) => {
    const bridgeData = await initializeBridgeData();
    const appMode = getAppModeFromQueryParams(
      new URLSearchParams(window.location.search),
    );
    const modeConfig = getModeConfig(appMode);

    await bootstrap({
      appMode,
      cloudId: bridgeData.cloudId || 'preview-cloud',
      identity: bridgeData.identity || {
        accountId: 'preview-user',
        accountType: 'atlassian' as any,
      },
      dependencies: modeConfig.dependencies,
      referencing: bridgeData.referencing,
      spaceId: '0',
      themeState: undefined,
    });

    dispatchInitialDatabaseLoad({
      contentId,
      contentReferenceId: contentId,
      spaceId: '0',
      spaceKey: 'PREVIEW',
      spaceName: 'Preview',
      title: 'Preview Database',
      isPreview: true,
    });

    const layoutComposition = createApplicationLayoutComposition();

    registerRecyclableModules(
      [
        initializeCurrentDatabaseSdk(),
        initializePermissionsManager(),
        initializeRecyclableDatabasePaywallFeatures(),
        layoutComposition.tableV2Lifecycle,
        initializeTableHighlightsController(layoutComposition.readIdentity),
        initializeRecyclableSelectionsMachine(layoutComposition.getSelectionDependencies),
      ],
      contentId,
      layoutComposition.readIdentity,
    );

    mount((getRoot) => {
      const root = getRoot();

      root.render(<AppWithLocale tableV2Host={layoutComposition.tableV2Host} />);
    });
  });

  await run();
};

try {
  start().catch((e) => {
    console.error('Failed to start databases preview:', e);
  });
} catch (e) {
  console.error('Failed to start databases preview:', e);
}
